import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '@env/environment';
import { IdName } from '@app-services/oem-dealer.service';
import { SelectOption } from '@app-shared/components/ui/select-option.model';
import {
  Regional,
  RegionalCreatePayload,
  RegionalUpdatePayload,
} from '../models/regional.model';

export interface LinkedRegional {
  id: string;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class RegionalService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/regional`;

  listSelectOptions(
    linkedRegional?: LinkedRegional | null,
  ): Observable<SelectOption<string | null>[]> {
    return this.listOptions().pipe(
      map((regionals) => this.buildSelectOptions(regionals, linkedRegional)),
    );
  }

  listOptions(): Observable<IdName[]> {
    return this.list(false).pipe(
      map((regionals) =>
        [...regionals]
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
          .map((regional) => ({
            id: regional.id,
            name: regional.name,
          })),
      ),
    );
  }

  list(includeInactive = false): Observable<Regional[]> {
    let params = new HttpParams();
    if (includeInactive) {
      params = params.set('includeInactive', 'true');
    }
    return this.http.get<Regional[]>(`${this.baseUrl}/list`, { params });
  }

  create(payload: RegionalCreatePayload): Observable<Regional> {
    return this.http.post<Regional>(this.baseUrl, payload);
  }

  update(id: string, payload: RegionalUpdatePayload): Observable<Regional> {
    return this.http.patch<Regional>(`${this.baseUrl}/${id}`, payload);
  }

  inactivate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  private buildSelectOptions(
    regionals: IdName[],
    linkedRegional?: LinkedRegional | null,
  ): SelectOption<string | null>[] {
    const options: SelectOption<string | null>[] = [
      { value: null, label: 'Nenhum' },
      ...regionals.map((regional) => ({
        value: regional.id,
        label: regional.name,
      })),
    ];

    if (linkedRegional && !options.some((option) => option.value === linkedRegional.id)) {
      options.push({
        value: linkedRegional.id,
        label: linkedRegional.name,
      });
    }

    return options;
  }
}
